import { useEffect, useState, useCallback, useMemo } from "react";
import { Edit, Trash, Megaphone, UploadCloudIcon, Eye } from "lucide-react";
import { DatePicker, Image, Select, Upload as AntUpload } from "antd";
import dayjs from "dayjs";
import { cn } from "../../utils/tailwindUtils";
import { checkUserPermissionFunc, disabledPastDate, truncateText } from "../../components/common/CommonFunctions";
import { Reactable } from "../../components/ui/reactable";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import {
  EMPLOYEE_DEPARTMENT,
  EMPLOYEE_PERSONAL_INFO,
  NOTICE_BOARD
} from "../../constants/api";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";

// Constants
const PRIORITY_LEVELS = ["High", "Medium", "Low"];
const STATUS_OPTIONS = ["Published", "Scheduled"];
const FILE_TYPES = {
  IMAGE: "image/",
  PDF: "application/pdf"
};
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Initial form state
const INITIAL_FORM_DATA = {
  notice_title: "",
  description: "",
  attachment: null,
  notice_to_departments: [],
  notice_to_employee: [],
  priority_level: "",
  status: "Published",
  published_date_time: "",
  expiry_date_time: ""
};

const NoticeBoard = () => {
  const { fetch, update, create } = useApiMethods();

  // State management
  const [noticeBoardData, setNoticeBoardData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serverTrigger, setServerTrigger] = useState(false);

  // Modal states
  const [openDialog, setOpenDialog] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Edit mode states
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // UI states
  const [isSelectorOpen, setSelectOpen] = useState(false);
  const [tooltipState, setTooltipState] = useState({
    show: false,
    values: [],
    position: { x: 0, y: 0 }
  });

  const { add: addPermission, draft: draftPermission, edit: editPermission } = checkUserPermissionFunc("administrator", "Notice Board", "SubMenu") || {};

  // Memoized values
  const allDepartmentValues = useMemo(() =>
    departmentsList.map(dept => dept.id),
    [departmentsList]
  );

  const displayDepartmentValues = useMemo(() => {
    if (formData.notice_to_departments.includes("all")) {
      return ["all"];
    }
    return formData.notice_to_departments;
  }, [formData.notice_to_departments]);

  // API calls
  const fetchDepartments = useCallback(async () => {
    try {
      const apiData = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' });
      if (apiData?.status === "success" && apiData.data) {
        setDepartmentsList(apiData.data);
      } else {
        setDepartmentsList([]);
        ToastMsg('error', 'Failed to load departments');
      }
    } catch (error) {
      ToastMsg('error', error.message);
      setDepartmentsList([]);
    }
  }, [fetch]);

  const fetchEmployees = useCallback(async () => {
    try {
      const apiData = await fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: 'True' });
      if (apiData?.status === "success" && apiData.data) {
        setEmployeeList(apiData.data);
      } else {
        setEmployeeList([]);
        ToastMsg('error', 'Failed to load employees');
      }
    } catch (error) {
      ToastMsg('error', error.message);
      setEmployeeList([]);
    }
  }, [fetch]);

  // Initial data loading
  useEffect(() => {
    fetchDepartments();
    fetchEmployees();
  }, []);

  // Department selection handler with "All" logic
  const handleDepartmentChange = useCallback((selectedValues) => {
    const currentValues = formData.notice_to_departments;

    // If "All" is being selected
    if (selectedValues.includes("all") && !currentValues.includes("all")) {
      setFormData(prev => ({
        ...prev,
        notice_to_departments: ["all"]
      }));
      setSelectOpen(false); // Close dropdown after selecting "All"
    }
    // If "All" is being deselected
    else if (!selectedValues.includes("all") && currentValues.includes("all")) {
      setFormData(prev => ({
        ...prev,
        notice_to_departments: []
      }));
    }
    // If individual departments are selected when "All" is currently selected
    else if (currentValues.includes("all") && selectedValues.length > 0) {
      // Remove "All" and select the individual department
      const filteredValues = selectedValues.filter(val => val !== "all");
      setFormData(prev => ({
        ...prev,
        notice_to_departments: filteredValues
      }));
    }
    // If selecting individual departments (normal case)
    else if (!selectedValues.includes("all")) {
      // If all departments are selected individually, auto-select "All"
      if (selectedValues.length === allDepartmentValues.length) {
        setFormData(prev => ({
          ...prev,
          notice_to_departments: ["all"]
        }));
        setSelectOpen(false);
      } else {
        setFormData(prev => ({
          ...prev,
          notice_to_departments: selectedValues
        }));
      }
    }
    // If "All" is in the selection but shouldn't be (edge case)
    else if (selectedValues.includes("all") && selectedValues.length > 1) {
      // Filter out individual departments, keep only "All"
      setFormData(prev => ({
        ...prev,
        notice_to_departments: ["all"]
      }));
    }
  }, [formData.notice_to_departments, allDepartmentValues]);

  // Form field handler
  const handleChange = useCallback((name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // File validation and handling
  const validateFile = (file) => {
    const isImage = file.type?.startsWith(FILE_TYPES.IMAGE);
    const isPDF = file.type === FILE_TYPES.PDF;

    if (!isImage && !isPDF) {
      ToastMsg("error", "Only image and PDF files are allowed!");
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      ToastMsg("error", "File size must be less than 5MB!");
      return false;
    }

    return true;
  };

  const handleFileChange = useCallback((file) => {
    if (!file || file.length === 0) {
      setFormData(prev => ({ ...prev, attachment: null }));
      return;
    }

    const uploadedFile = Array.isArray(file) ? file[0] : file;

    if (validateFile(uploadedFile)) {
      setFormData(prev => ({ ...prev, attachment: uploadedFile }));
    }
  }, []);

  // Tooltip handlers
  const handleTooltipToggle = useCallback((show, values = [], position = { x: 0, y: 0 }) => {
    setTooltipState({
      show,
      values,
      position
    });
  }, []);

  const renderMaxTagPlaceholder = useCallback((omittedValues) => {
    // If "All" is selected, show different tooltip text
    if (formData.notice_to_departments.includes("all")) {
      return (
        <span
          onMouseEnter={(e) => {
            const rect = e.target.getBoundingClientRect();
            handleTooltipToggle(true, [{ key: "all", label: "All Departments" }], {
              x: rect.left,
              y: rect.bottom
            });
          }}
          onMouseLeave={() => handleTooltipToggle(false)}
          className="cursor-pointer"
        >
          All Departments
        </span>
      );
    }

    return (
      <span
        onMouseEnter={(e) => {
          const rect = e.target.getBoundingClientRect();
          handleTooltipToggle(true, omittedValues, {
            x: rect.left,
            y: rect.bottom
          });
        }}
        onMouseLeave={() => handleTooltipToggle(false)}
        className="cursor-pointer"
      >
        +{omittedValues.length} more
      </span>
    );
  }, [formData.notice_to_departments, handleTooltipToggle]);

  // Get the actual selected values for the API (convert "all" to all department IDs)
  const getApiDepartmentValues = useCallback(() => {
    if (formData.notice_to_departments.includes("all")) {
      return allDepartmentValues;
    }
    return formData.notice_to_departments;
  }, [formData.notice_to_departments, allDepartmentValues]);

  // Form reset
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setOpenDialog(false);
    setEditMode(false);
    setEditId(null);
    setSelectedRow(null);
  }, []);

  // Edit handler
  const handleEdit = useCallback((row) => {

    // Merge expiry_date + expiry_time into one datetime string
    const expiryDateTime = row.expiry_date && row.expiry_time
      ? dayjs(`${row.expiry_date}T${row.expiry_time}`)
      : "";

    const publishedDateTime = row.published_date && row.published_time
      ? dayjs(`${row.published_date}T${row.published_time}`)
      : "";

    setEditMode(true);
    setEditId(row.id);
    setSelectedRow(row);
    setFormData({
      notice_title: row.notice_title || "",
      description: row.description || "",
      attachment: row.attachment || null,
      notice_to_departments: row.notice_to_departments || [],
      notice_to_employee: row.notice_to_employee || [],
      priority_level: row.priority_level || "",
      status: row.status || "Published",
      published_date_time: publishedDateTime,
      expiry_date_time: expiryDateTime
    });
    setOpenDialog(true);
  }, []);

  // Delete handler
  const handleDeleteClick = useCallback((row) => {
    setSelectedRow(row);
    setIsDeleteConfirmOpen(true);
  }, []);

  // Form validation
  const validateForm = useCallback((isDraft = false) => {
    if (!formData.notice_title.trim()) {
      ToastMsg("error", "Notice title is required!");
      return false;
    }

    if (!formData.description.trim()) {
      ToastMsg("error", "Notice description is required!");
      return false;
    }

    if (formData.notice_to_departments.length === 0) {
      ToastMsg("error", "Please select at least one department!");
      return false;
    }

    if (!isDraft) {
      if (formData.status === "Scheduled" && !formData.published_date_time) {
        ToastMsg("error", "Publish date & time is required for scheduled notices!");
        return false;
      }

      if (!formData.expiry_date_time) {
        ToastMsg("error", "Expiry date & time is required!");
        return false;
      }
    }

    return true;
  }, [formData]);

  // Prepare form data for API
  const prepareFormData = useCallback((isDraft = false) => {
    const cleanedData = Object.fromEntries(
      Object.entries(formData).filter(([_, value]) =>
        value !== undefined &&
        !(Array.isArray(value) && value.length === 0) &&
        value !== null
      )
    );

    const formDataObj = new FormData();


    if (editMode) {
      formDataObj.append("id", editId);
    }

    const processedData = {
      ...cleanedData,
      status: isDraft ? "Draft" : cleanedData.status,
      is_draft: isDraft
    };

    const actualDepartmentValues = getApiDepartmentValues();
    if (actualDepartmentValues.length > 0) {
      processedData.notice_to_departments = actualDepartmentValues;
    }

    // Handle date formatting
    if (processedData.published_date_time && processedData.status === "Scheduled") {
      processedData.published_date = dayjs(processedData.published_date_time).format('YYYY-MM-DD');
      processedData.published_time = dayjs(processedData.published_date_time).format('HH:mm:ss');
      delete processedData.published_date_time;
    }

    if (processedData.expiry_date_time) {
      processedData.expiry_date = dayjs(processedData.expiry_date_time).format('YYYY-MM-DD');
      processedData.expiry_time = dayjs(processedData.expiry_date_time).format('HH:mm:ss');
      delete processedData.expiry_date_time;
    }

    // Handle arrays
    if (processedData.notice_to_departments?.length > 0) {
      processedData.notice_to_departments = JSON.stringify(processedData.notice_to_departments);
    }

    if (processedData.notice_to_employee?.length > 0) {
      processedData.notice_to_employee = JSON.stringify(processedData.notice_to_employee);
    }

    // Handle file
    if (processedData.attachment && typeof processedData.attachment !== "string") {
      processedData.attachment = processedData.attachment.originFileObj || processedData.attachment;
    }

    // Append all fields to FormData
    Object.entries(processedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formDataObj.append(key, value);
      }
    });

    return formDataObj;
  }, [formData, editMode, editId]);

  // Save handler
  const handleSave = useCallback(async (isDraft = false) => {
    if (!validateForm(isDraft)) return;

    setLoading(true);

    try {
      const formDataObj = prepareFormData(isDraft);
      const apiMethod = editMode ? update : create;

      const response = await apiMethod(NOTICE_BOARD, formDataObj, true);

      if (response?.status === "success") {
        ToastMsg("success", response.message);
        resetForm();
        setServerTrigger(prev => !prev);
        fetchDepartments()
        fetchEmployees()
      } else {
        ToastMsg("error", response?.message || "Operation failed");
      }
    } catch (error) {
      ToastMsg("error", error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [validateForm, prepareFormData, editMode, update, create, resetForm]);

  // Delete confirmation handler
  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRow) return;

    setLoading(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append("id", selectedRow.id);
      formDataObj.append("is_active", false);
      formDataObj.append("draft_delete", true);
      formDataObj.append("is_draft", true);

      const response = await update(NOTICE_BOARD, formDataObj);

      if (response?.status === "success") {
        ToastMsg("success", "Notice deleted successfully");
        setServerTrigger(prev => !prev);
        setIsDeleteConfirmOpen(false);
        setSelectedRow(null);
      } else {
        ToastMsg("error", response?.message || "Delete failed");
      }
    } catch (error) {
      ToastMsg("error", error.message || "Delete error");
    } finally {
      setLoading(false);
    }
  }, [selectedRow, update]);

  // Table columns definition
  const noticeBoardColumns = useMemo(() => [
    {
      accessor: "notice_title",
      Header: "Notice Title",
      sortable: true,
      Cell: ({ value }) => value ? truncateText(value, 30, "300px", "balance") : ""
    },
    {
      accessor: "description",
      Header: "Description",
      Cell: ({ value }) => truncateText(value, 22, "300px", "balance")
    },
    {
      accessor: "published_date",
      Header: "Published Date",
      filterable: true,
      Cell: ({ value }) => value ?? "-",
    },
    {
      accessor: "status",
      Header: "Status",
      filterable: true,
      Cell: ({ value }) => {
        const statusConfig = {
          Published: { class: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" },
          Scheduled: { class: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300" },
          Draft: { class: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300" }
        };

        const config = statusConfig[value] || { class: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300" };

        return (
          <span className={cn("px-2 py-1 rounded-full text-xs font-medium", config.class)}>
            {value ?? "-"}
          </span>
        );
      },
    },
    {
      accessor: "notice_by",
      Header: "Notice by",
      filterable: true,
      Cell: ({ value }) => value ?? "-",
    },
    ...(editPermission
      ? [
        {
          accessor: "action",
          Header: "Actions",
          sortable: false,
          Cell: ({ row }) => (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                onClick={() => handleEdit(row)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              {row?.is_draft && draftPermission && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                  onClick={() => handleDeleteClick(row)}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              )}
            </div>
          ),
        },
      ]
      : []),
  ], [editPermission, draftPermission, handleEdit, handleDeleteClick]);

  return (
    <ContainerCard>
      {/* Header */}
      <ModuleTitleCard Icon={Megaphone} title={'Notice Board'} subtitle="Broadcast messages and important updates to the team" totalRecords={totalRecords} buttonEnable={!!addPermission} setOpenDialog={setOpenDialog} buttonName={'Add Notice'} />

      {/* Data Table */}
      <Reactable
        columns={noticeBoardColumns}
        data={noticeBoardData}
        setData={setNoticeBoardData}
        setTotalCount={setTotalRecords}
        url={NOTICE_BOARD}
        serverSide={true}
        serverTrigger={serverTrigger}
        tableHeight="calc(100vh - 320px)"
        rowSize={editPermission ? "sm" : null}
        isCSVRequired={true}
        isPDFRequired={true}
        isDraftRequired={!!draftPermission}
      />

      {/* Add/Edit Modal */}
      <DialogModal
        open={openDialog}
        onClose={resetForm}
        onDraft={() => handleSave(true)}
        onSave={() => handleSave(false)}
        title={editMode ? "Edit Notice" : "Add Notice"}
        closeLabel="Cancel"
        saveLabel={editMode ? "Update" : "Save"}
        draftlabel="Save as Draft"
        editMode={editMode}
        loading={loading}
        width="800px"
      >
        <div className={cn("grid gap-8 grid-cols-1 md:grid-cols-2", "max-h-90 overflow-y-auto pr-2")}>

          {/* Left Column */}
          <div className="space-y-4">
            {/* Notice Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Notice Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.notice_title}
                onChange={(e) => handleChange("notice_title", e.target.value)}
                placeholder="Enter notice title"
                disabled={loading}
                className={cn(
                  "w-full rounded-md transition-colors duration-200",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                  "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                  "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                )}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Notice Description <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Enter description"
                rows={4}
                showCharCount={true}
                maxLength={250}
                disabled={loading}
                className={cn(
                  "w-full rounded-md transition-colors duration-200",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                  "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                  "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                )}
              />
            </div>

            {/* Departments Select */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Department <span className="text-red-500">*</span>
              </label>
              <Select
                showSearch
                mode="multiple"
                showArrow={true}
                size="large"
                style={{ width: "100%" }}
                value={displayDepartmentValues}
                onChange={handleDepartmentChange}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                placeholder="Select departments"
                data-testid="nb_in_departments"
                open={isSelectorOpen}
                onDropdownVisibleChange={(isOpen) => setSelectOpen(isOpen)}
                maxTagCount={1} // Show only 1 tag (either "All" or first department)
                maxTagPlaceholder={renderMaxTagPlaceholder}
                className={cn(
                  "w-full rounded-md transition-colors duration-200",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                  "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                  "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                )}
              >
                {departmentsList.length > 0 && (
                  <Select.Option key="all" value="all">
                    All Departments
                  </Select.Option>
                )}
                {departmentsList.map(department => (
                  <Select.Option
                    key={department.id}
                    value={department.id}
                    disabled={formData.notice_to_departments.includes('all')}
                  >
                    {department.department_name}
                  </Select.Option>
                ))}
              </Select>

              {/* Enhanced Tooltip */}
              {tooltipState.show && tooltipState.values.length > 0 && (
                <div
                  className="absolute z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg max-w-xs"
                  style={{
                    top: tooltipState.position.y + 10,
                    left: tooltipState.position.x
                  }}
                >
                  {formData.notice_to_departments.includes("all") ? (
                    <div>All Departments selected</div>
                  ) : (
                    tooltipState.values.map(val => (
                      <div key={val.key} className="truncate">{val.label}</div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Employees */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Notice to Employee
              </label>
              <Select
                mode="multiple"
                size="large"
                value={formData.notice_to_employee}
                onChange={(value) => handleChange("notice_to_employee", value)}
                placeholder="Select employees"
                maxTagCount={2}
                className={cn(
                  "w-full rounded-md transition-colors duration-200",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                  "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                  "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                )}
              >
                {employeeList
                  .filter(emp => emp.is_active)
                  .map(emp => (
                    <Select.Option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </Select.Option>
                  ))
                }
              </Select>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {/* File Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Notice Upload
              </label>
              <div>
                {!formData.attachment && (
                  <AntUpload.Dragger
                    accept="image/*,.pdf"
                    maxCount={1}
                    beforeUpload={() => false}
                    onChange={({ file }) => handleFileChange(file)}
                    showUploadList={false}
                    className="dark:bg-gray-800 dark:border-gray-700"
                  >
                    <UploadCloudIcon className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Click to upload document
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Supported formats: .doc, .docx (Max: 1MB)
                    </p>
                  </AntUpload.Dragger>
                )}

                {/* File Preview */}
                {formData.attachment && (
                  <div className="mt-2">
                    {formData.attachment.type?.startsWith("image/") ? (
                      // IMAGE PREVIEW
                      <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                        <Image
                          src={URL.createObjectURL(formData.attachment)}
                          alt="preview"
                          width={120}
                          className="rounded-md shadow-sm"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChange("attachment", null)}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : formData.attachment.type === "application/pdf" ? (
                      // PDF PREVIEW
                      <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                        <a
                          href={URL.createObjectURL(formData.attachment)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sidebar underline underline-offset-2 text-sm flex items-center gap-2"
                        >
                          Preview PDF <Eye className="w-3 h-3"/>
                        </a>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChange("attachment", null)}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      // OTHER FILE PREVIEW (DOC, DOCX, ETC.)
                      <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {formData.attachment.name}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChange("attachment", null)}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Publish Schedule */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                When to publish? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                {STATUS_OPTIONS.map(status => (
                  <Button
                    key={status}
                    type="button"
                    size="sm"
                    onClick={() => handleChange("status", status)}
                    className={cn(
                      "px-3 py-2 rounded text-sm font-medium border transition-colors",
                      formData.status === status
                        ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900 dark:text-green-300 hover:bg-sidebar-primary hover:text-white"
                        : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                    )}
                    disabled={loading}
                  >
                    {status === "Published" ? "Publish Now" : "Schedule"}
                  </Button>
                ))}
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid gap-4 grid-cols-2">
              {/* Publish Date (for scheduled) */}
              {formData.status === "Scheduled" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Publish Date & Time <span className="text-red-500">*</span>
                  </label>
                  <DatePicker
                    showTime
                    value={formData.published_date_time}
                    onChange={(date) => handleChange("published_date_time", date)}
                    format="DD-MM-YYYY HH:mm"
                    disabledDate={disabledPastDate}
                    className={cn(
                      "w-full rounded-md transition-colors duration-200",
                      "border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                      "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                      "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                    )}
                    size="large"
                  />
                </div>
              )}

              {/* Expiry Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Expiry Date & Time <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  showTime
                  value={formData.expiry_date_time}
                  onChange={(value) => handleChange("expiry_date_time", value)}
                  format="DD-MM-YYYY HH:mm"
                  disabledDate={disabledPastDate}
                  className={cn(
                    "w-full rounded-md transition-colors duration-200",
                    "border-gray-300 dark:border-gray-600",
                    "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                  )}
                  size="large"
                />
              </div>
            </div>

            {/* Priority Level (only when employees selected) */}
            {formData.notice_to_employee.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Priority Level
                </label>
                <div className="flex gap-2">
                  {PRIORITY_LEVELS.map(level => (
                    <Button
                      key={level}
                      type="button"
                      onClick={() => handleChange("priority_level", level)}
                      className={cn(
                        "px-3 py-2 rounded text-sm font-medium border transition-colors flex-1",
                        formData.priority_level === level
                          ? level === 'High'
                            ? "bg-red-100 text-red-700 border-red-300 dark:bg-red-900 dark:text-red-300 hover:bg-sidebar-primary hover:text-white"
                            : level === 'Medium'
                              ? "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-300 hover:bg-sidebar-primary hover:text-white"
                              : "bg-green-100 text-green-700 border-green-300 dark:bg-green-900 dark:text-green-300 hover:bg-sidebar-primary hover:text-white"
                          : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                      )}
                      disabled={loading}
                      size="sm"
                    >
                      {level}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogModal>

      {/* Delete Confirmation Modal */}
      <ConfirmPopupModal
        open={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setSelectedRow(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Notice"
        description="Are you sure you want to delete this notice? This action cannot be undone."
        type="warning"
        confirmlabel="Delete"
        destructive={true}
        loading={loading}
      />
    </ContainerCard>
  );
};

export default NoticeBoard;